/**
 * Phase 326m UI — Stuck User Triage panel.
 *
 * Wraps the two super-admin diagnostic endpoints into a single panel:
 *   1. Search box → GET /api/super-admin/diagnose-login
 *      → shows user/student/employee rows + a likely_cause triage label.
 *   2. "Force Reset Credentials" button → POST /api/super-admin/force-reset-credentials
 *      → returns a fresh temp password, displayed prominently with a copy button.
 *
 * Built to be production-safe: never leaks the bcrypt hash (the backend
 * strips it), never auto-fires writes, and confirms before any reset.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, Search, KeyRound, Copy, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// Triage labels → friendly headlines + colour palette.
const CAUSE_META = {
  users_row_missing_run_login_backfill_or_force_reset: {
    label: "User row missing",
    detail: "There's a student/employee record but no login account. Hit 'Force Reset' to create it.",
    tone: "amber",
  },
  user_row_exists_but_password_hash_blank_force_reset: {
    label: "Password hash blank",
    detail: "Their account exists but has no password set. Hit 'Force Reset' to issue a fresh temp.",
    tone: "amber",
  },
  multi_role_disambiguation_required_role_must_be_specified: {
    label: "Multiple roles share this email",
    detail: "This email is bound to >1 account. The user must pick a role on the login screen.",
    tone: "blue",
  },
  no_record_for_this_email_check_spelling: {
    label: "No record found",
    detail: "Nobody with this email exists. Double-check spelling / case.",
    tone: "red",
  },
  user_row_looks_healthy_password_likely_mistyped_or_changed: {
    label: "Account looks healthy",
    detail: "User row + password are present. They're likely mistyping or forgot it. Force-reset to issue a fresh temp.",
    tone: "emerald",
  },
};

const TONE_CLASSES = {
  red:     "border-red-200 bg-red-50 text-red-800",
  amber:   "border-amber-200 bg-amber-50 text-amber-900",
  blue:    "border-blue-200 bg-blue-50 text-blue-900",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
};

export default function StuckUserTriagePanel() {
  const [email, setEmail] = useState('');
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const runDiagnose = async (e) => {
    e?.preventDefault?.();
    const target = email.trim().toLowerCase();
    if (!target) {
      toast.error('Enter an email to diagnose');
      return;
    }
    setDiagnosing(true);
    setResetResult(null);
    try {
      const res = await api.get('/super-admin/diagnose-login', { params: { email: target } });
      setDiagnosis(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Diagnose failed';
      toast.error(typeof detail === 'string' ? detail : 'Diagnose failed');
      setDiagnosis(null);
    } finally {
      setDiagnosing(false);
    }
  };

  const runForceReset = async () => {
    const target = email.trim().toLowerCase();
    if (!target) return;
    setResetting(true);
    setConfirming(false);
    try {
      const res = await api.post('/super-admin/force-reset-credentials', { email: target });
      setResetResult(res.data);
      toast.success(`New temp password issued for ${target}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Force-reset failed';
      toast.error(typeof detail === 'string' ? detail : 'Force-reset failed');
    } finally {
      setResetting(false);
    }
  };

  const copyTempPassword = () => {
    if (!resetResult?.new_temp_password) return;
    navigator.clipboard.writeText(resetResult.new_temp_password)
      .then(() => toast.success('Temp password copied'))
      .catch(() => toast.error('Copy failed'));
  };

  const causeMeta = diagnosis?.likely_cause ? CAUSE_META[diagnosis.likely_cause] : null;
  const hasAnyRecord = diagnosis && (
    diagnosis.user_rows?.length > 0
    || diagnosis.student_rows?.length > 0
    || diagnosis.employee_rows?.length > 0
  );

  return (
    <Card
      className="border-amber-200 bg-gradient-to-br from-amber-50/40 to-white"
      data-testid="stuck-user-triage-panel"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-600" />
          Stuck User Triage
          <Badge variant="outline" className="text-[10px] tracking-wider ml-auto">
            SUPPORT TOOL
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">

        {/* ─── Search box ─── */}
        <form onSubmit={runDiagnose} className="flex gap-2" data-testid="triage-search-form">
          <Input
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 text-sm"
            data-testid="triage-email-input"
          />
          <Button
            type="submit"
            size="sm"
            disabled={diagnosing}
            data-testid="triage-diagnose-btn"
          >
            {diagnosing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Diagnose
          </Button>
        </form>

        {/* ─── Diagnosis result ─── */}
        {diagnosis && (
          <div className="space-y-3" data-testid="triage-diagnosis-result">
            {/* Cause headline */}
            <div className={`rounded-lg border p-3 ${TONE_CLASSES[causeMeta?.tone] || TONE_CLASSES.amber}`}>
              <div className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                {causeMeta?.tone === 'emerald'
                  ? <CheckCircle2 className="h-3.5 w-3.5" />
                  : <AlertTriangle className="h-3.5 w-3.5" />}
                {causeMeta?.label || diagnosis.likely_cause}
              </div>
              <p className="text-xs mt-1.5 leading-relaxed">{causeMeta?.detail || ''}</p>
            </div>

            {/* Row counters */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-white border p-2.5" data-testid="triage-counter-users">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Login</p>
                <p className="text-lg font-display font-semibold tabular-nums">{diagnosis.user_rows?.length ?? 0}</p>
              </div>
              <div className="rounded-lg bg-white border p-2.5" data-testid="triage-counter-students">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Student</p>
                <p className="text-lg font-display font-semibold tabular-nums">{diagnosis.student_rows?.length ?? 0}</p>
              </div>
              <div className="rounded-lg bg-white border p-2.5" data-testid="triage-counter-employees">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Employee</p>
                <p className="text-lg font-display font-semibold tabular-nums">{diagnosis.employee_rows?.length ?? 0}</p>
              </div>
            </div>

            {/* Per-row details (compact) */}
            {diagnosis.user_rows?.length > 0 && (
              <details className="text-xs" data-testid="triage-rows-users">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-1">
                  Login row(s)
                </summary>
                <pre className="bg-slate-50 rounded p-2 mt-1 overflow-x-auto text-[11px] leading-snug">
                  {JSON.stringify(diagnosis.user_rows, null, 2)}
                </pre>
              </details>
            )}
            {diagnosis.student_rows?.length > 0 && (
              <details className="text-xs" data-testid="triage-rows-students">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-1">
                  Student row(s)
                </summary>
                <pre className="bg-slate-50 rounded p-2 mt-1 overflow-x-auto text-[11px] leading-snug">
                  {JSON.stringify(diagnosis.student_rows, null, 2)}
                </pre>
              </details>
            )}
            {diagnosis.employee_rows?.length > 0 && (
              <details className="text-xs" data-testid="triage-rows-employees">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-1">
                  Employee row(s)
                </summary>
                <pre className="bg-slate-50 rounded p-2 mt-1 overflow-x-auto text-[11px] leading-snug">
                  {JSON.stringify(diagnosis.employee_rows, null, 2)}
                </pre>
              </details>
            )}

            {/* Action: Force-reset (only when there's something to reset) */}
            {hasAnyRecord && (
              <div className="pt-1">
                {!confirming ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirming(true)}
                    disabled={resetting}
                    className="w-full"
                    data-testid="triage-force-reset-btn"
                  >
                    <KeyRound className="h-4 w-4 mr-2" />
                    Force Reset &amp; Send Credentials
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={runForceReset}
                      disabled={resetting}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      data-testid="triage-force-reset-confirm-btn"
                    >
                      {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
                      Yes — issue fresh temp password
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirming(false)}
                      disabled={resetting}
                      data-testid="triage-force-reset-cancel-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
                  Rotates password, invalidates existing sessions, re-sends credentials by email + WhatsApp.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ─── Reset result ─── */}
        {resetResult && (
          <div
            className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3"
            data-testid="triage-reset-result"
          >
            <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 uppercase tracking-wider mb-2">
              <CheckCircle2 className="h-3.5 w-3.5" />
              New temp password issued
            </div>
            <div className="flex items-center gap-2 bg-white rounded border border-emerald-200 px-3 py-2.5">
              <code className="font-mono text-sm font-semibold text-emerald-900 flex-1 tabular-nums tracking-wide"
                    data-testid="triage-new-temp-password">
                {resetResult.new_temp_password}
              </code>
              <Button size="sm" variant="ghost" onClick={copyTempPassword} data-testid="triage-copy-temp-btn">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="text-xs text-emerald-800 mt-2 leading-relaxed">
              📩 Credentials email <strong>{resetResult.dispatch?.credentials?.status || '—'}</strong>,
              WhatsApp <strong>{resetResult.dispatch?.whatsapp?.status || '—'}</strong>.
              Tell the user to log in at <span className="font-mono">out-play.in/login</span> —
              they&apos;ll be forced to pick a new password on first login.
            </p>
            {resetResult.created_user_row && (
              <p className="text-[10px] text-emerald-700 mt-1.5 italic">
                ✓ Created the missing login account from scratch (their student/employee row was already there).
              </p>
            )}
          </div>
        )}

      </CardContent>
    </Card>
  );
}
